import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SiteImage } from "./SiteImage";

/**
 * ImageGallery with Lightbox
 * Features:
 * - Swipe navigation on mobile
 * - Keyboard navigation (arrow keys, ESC)
 * - Body scroll lock (mobile-safe)
 * - Focus management (returns focus on close)
 * - Image preloading for next/prev
 * - Counter display
 * - Respects prefers-reduced-motion
 * - Uses 100dvh for mobile viewport safety
 * - Micro-feedback on tap (mobile-first)
 */
export const ImageGallery = ({ 
  images = [], 
  columns = 3, 
  showLightbox = true,
  showPlaceholderBadge = false 
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null); // For cross-fade
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [viewportHeight, setViewportHeight] = useState("100vh");
  const [tappedIndex, setTappedIndex] = useState(null); // For tap feedback
  const [isCrossFading, setIsCrossFading] = useState(false); // For cross-fade
  
  // Refs for focus management and scroll position
  const triggerRef = useRef(null);
  const lightboxRef = useRef(null);
  const closeButtonRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const tapTimeoutRef = useRef(null);
  const crossFadeTimeoutRef = useRef(null);
  
  // Check reduced motion preference
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && 
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Cross-fade transition duration (ms)
  const CROSSFADE_DURATION = 300;

  // Minimum swipe distance to trigger navigation
  const minSwipeDistance = 50;

  // Calculate viewport height (handles mobile address bar)
  useEffect(() => {
    const updateViewportHeight = () => {
      // Use dvh if supported, fallback to window.innerHeight
      if (CSS.supports("height", "100dvh")) {
        setViewportHeight("100dvh");
      } else {
        setViewportHeight(`${window.innerHeight}px`);
      }
    };
    
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  // Open lightbox with tap feedback
  const openLightbox = (index, event) => {
    if (!showLightbox) return;
    triggerRef.current = event?.currentTarget;
    scrollPositionRef.current = window.scrollY;
    
    // Clear any pending timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    
    // Apply tap feedback (if motion is allowed)
    if (!prefersReducedMotion.current) {
      setTappedIndex(index);
      
      // Open lightbox after brief feedback (80ms)
      tapTimeoutRef.current = setTimeout(() => {
        setTappedIndex(null);
        setCurrentIndex(index);
        setLightboxOpen(true);
      }, 80);
    } else {
      // No feedback delay for reduced motion
      setCurrentIndex(index);
      setLightboxOpen(true);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      if (crossFadeTimeoutRef.current) {
        clearTimeout(crossFadeTimeoutRef.current);
      }
    };
  }, []);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setSwipeOffset(0);
    setIsCrossFading(false);
    setPreviousIndex(null);
    if (crossFadeTimeoutRef.current) {
      clearTimeout(crossFadeTimeoutRef.current);
    }
  }, []);

  // Navigate with true cross-fade (both images visible simultaneously)
  const navigateWithCrossFade = useCallback((newIndex) => {
    if (newIndex === currentIndex) return;
    if (isCrossFading) return; // Prevent navigation during transition
    
    if (prefersReducedMotion.current) {
      // No animation for reduced motion - instant change
      setCurrentIndex(newIndex);
      setSwipeOffset(0);
      return;
    }

    // Clear any pending transition
    if (crossFadeTimeoutRef.current) {
      clearTimeout(crossFadeTimeoutRef.current);
    }

    // Store current as previous for cross-fade overlay
    setPreviousIndex(currentIndex);
    setCurrentIndex(newIndex);
    setSwipeOffset(0);
    
    // Start cross-fade
    setIsCrossFading(true);
    
    // End transition after animation completes
    crossFadeTimeoutRef.current = setTimeout(() => {
      setIsCrossFading(false);
      setPreviousIndex(null);
    }, CROSSFADE_DURATION + 50);
  }, [currentIndex, CROSSFADE_DURATION, isCrossFading]);

  // Navigate to next image
  const goNext = useCallback(() => {
    const newIndex = (currentIndex + 1) % images.length;
    navigateWithCrossFade(newIndex);
  }, [currentIndex, images.length, navigateWithCrossFade]);

  // Navigate to previous image
  const goPrev = useCallback(() => {
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    navigateWithCrossFade(newIndex);
  }, [currentIndex, images.length, navigateWithCrossFade]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowRight") goNext();
    if (e.key === "ArrowLeft") goPrev();
  }, [closeLightbox, goNext, goPrev]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    
    // Calculate offset for visual feedback (only if not reduced motion)
    if (!prefersReducedMotion.current && touchStart) {
      const offset = currentTouch - touchStart;
      // Limit offset to prevent over-dragging
      setSwipeOffset(Math.max(-100, Math.min(100, offset)));
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && images.length > 1) {
      goNext();
    } else if (isRightSwipe && images.length > 1) {
      goPrev();
    } else {
      setSwipeOffset(0);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Body scroll lock when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      // Save scroll position
      const savedScrollY = scrollPositionRef.current;
      
      // Add class to html and body to lock scroll
      document.documentElement.classList.add("lightbox-open");
      document.body.classList.add("lightbox-open");
      
      // Focus the close button
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      
      return () => {
        // Remove classes
        document.documentElement.classList.remove("lightbox-open");
        document.body.classList.remove("lightbox-open");
        
        // Restore scroll position
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollY);
          // Return focus to trigger
          triggerRef.current?.focus();
        });
      };
    }
  }, [lightboxOpen]);

  // Preload next and previous images
  useEffect(() => {
    if (!lightboxOpen || images.length <= 1) return;
    
    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    
    // Preload next image
    const nextImg = new Image();
    nextImg.src = images[nextIndex]?.url;
    
    // Preload previous image
    const prevImg = new Image();
    prevImg.src = images[prevIndex]?.url;
  }, [lightboxOpen, currentIndex, images]);

  // Grid column classes
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  // Animation/transition classes based on motion preference
  const transitionClass = prefersReducedMotion.current 
    ? "" 
    : "transition-all duration-200 ease-out";

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Ingen billeder tilføjet endnu.
      </div>
    );
  }

  return (
    <>
      {/* Gallery Grid */}
      <div data-testid="image-gallery" className={`grid ${gridCols[columns] || gridCols[3]} gap-6`}>
        {images.map((image, index) => {
          const isTapped = tappedIndex === index;
          
          return (
            <button
              key={image.id || index}
              data-testid={`gallery-image-${index}`}
              className={`relative overflow-hidden group cursor-pointer text-left rounded-lg
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3C34] focus-visible:ring-offset-2
                ${showPlaceholderBadge && image.is_placeholder ? "placeholder-indicator" : ""}
                ${prefersReducedMotion.current ? "" : "transition-transform duration-100 ease-out"}
              `}
              style={{
                transform: isTapped ? "scale(0.98)" : "scale(1)",
                opacity: isTapped ? 0.92 : 1,
              }}
              onClick={(e) => openLightbox(index, e)}
              aria-label={`Åbn billede ${index + 1}: ${image.alt || image.caption || "Galleri billede"}`}
            >
              <SiteImage
                src={image.url}
                alt={image.alt || `Billede ${index + 1}`}
                aspectRatio="4/3"
                className={`${prefersReducedMotion.current ? "" : "transition-transform duration-500 group-hover:scale-105"}`}
                loading="lazy"
              />
              {image.caption && (
                <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 ${
                  prefersReducedMotion.current 
                    ? "opacity-0 group-hover:opacity-100" 
                    : "translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                }`}>
                  <p className="text-white text-sm font-medium">{image.caption}</p>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Lightbox Modal - Rendered via Portal to escape transform stacking context */}
      {lightboxOpen && createPortal(
        <div
          ref={lightboxRef}
          data-testid="lightbox"
          className="lightbox-overlay bg-black"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={`Billede ${currentIndex + 1} af ${images.length}`}
        >
          {/* Inner container - ensures centering */}
          <div 
            className="w-full h-full flex flex-col"
          >
            {/* Header bar with close and counter */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 md:px-6 md:py-4 z-20">
              {/* Counter */}
              <div className="text-white/70 text-sm md:text-base">
                {currentIndex + 1} / {images.length}
              </div>
              
              {/* Close button */}
              <button
                ref={closeButtonRef}
                data-testid="lightbox-close"
                onClick={closeLightbox}
                className={`text-white hover:text-gray-300 p-1 ${transitionClass}`}
                aria-label="Luk lightbox"
              >
                <X size={28} className="md:w-8 md:h-8" />
              </button>
            </div>

            {/* Main content area - flex-grow to fill remaining space */}
            <div 
              className="flex-grow flex items-center justify-center relative overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Navigation arrows (desktop only) */}
              {images.length > 1 && (
                <>
                  <button
                    data-testid="lightbox-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      goPrev();
                    }}
                    className={`absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-20 p-2 hidden md:flex items-center justify-center ${transitionClass}`}
                    aria-label="Forrige billede"
                  >
                    <ChevronLeft size={48} />
                  </button>
                  <button
                    data-testid="lightbox-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      goNext();
                    }}
                    className={`absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-20 p-2 hidden md:flex items-center justify-center ${transitionClass}`}
                    aria-label="Næste billede"
                  >
                    <ChevronRight size={48} />
                  </button>
                </>
              )}

              {/* Image wrapper with swipe transform */}
              <div
                className="flex-1 flex items-center justify-center px-4 md:px-20 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{
                  transform: prefersReducedMotion.current ? "none" : `translateX(${swipeOffset}px)`,
                  transition: swipeOffset === 0 ? (prefersReducedMotion.current ? "none" : "transform 200ms ease-out") : "none",
                }}
              >
                {/* Image stack container - enables true cross-fade */}
                <div className="relative w-full h-full flex items-center justify-center">
                  
                  {/* Previous image (fading out during transition) */}
                  {previousIndex !== null && isCrossFading && previousIndex !== currentIndex && (
                    <div 
                      className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0 animate-lightboxFadeOut"
                      style={{ animationDuration: `${CROSSFADE_DURATION}ms` }}
                    >
                      <img
                        src={images[previousIndex]?.url}
                        alt=""
                        className="max-w-full object-contain select-none"
                        style={{ 
                          maxHeight: "calc(100dvh - 160px)",
                          maxHeight: "calc(100vh - 160px)",
                        }}
                        draggable={false}
                      />
                    </div>
                  )}
                  
                  {/* Current image (fading in or fully visible) */}
                  <div 
                    className={`relative z-10 flex flex-col items-center justify-center ${
                      isCrossFading && previousIndex !== null ? "animate-lightboxFadeIn" : "opacity-100"
                    }`}
                    style={isCrossFading && previousIndex !== null ? { animationDuration: `${CROSSFADE_DURATION}ms` } : {}}
                  >
                    <img
                      src={images[currentIndex]?.url}
                      alt={images[currentIndex]?.alt || `Billede ${currentIndex + 1}`}
                      className="max-w-full object-contain select-none"
                      style={{ 
                        maxHeight: "calc(100dvh - 160px)",
                        maxHeight: "calc(100vh - 160px)",
                      }}
                      draggable={false}
                    />
                    {images[currentIndex]?.caption && (
                      <p className="text-white text-center mt-3 text-sm md:text-base px-4 max-w-2xl">
                        {images[currentIndex].caption}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Crossfade animation styles - matching GalleryPage */}
                <style>{`
                  @keyframes lightboxFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                  }
                  @keyframes lightboxFadeOut {
                    from { opacity: 1; }
                    to { opacity: 0; }
                  }
                  .animate-lightboxFadeIn {
                    animation: lightboxFadeIn ${CROSSFADE_DURATION}ms ease-out forwards;
                  }
                  .animate-lightboxFadeOut {
                    animation: lightboxFadeOut ${CROSSFADE_DURATION}ms ease-out forwards;
                  }
                `}</style>
              </div>
            </div>

            {/* Footer area for mobile hints and dots */}
            <div className="flex-shrink-0 pb-4 md:pb-6">
              {/* Mobile navigation dots */}
              {images.length > 1 && images.length <= 12 && (
                <div className="flex gap-1.5 justify-center md:hidden">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentIndex(idx);
                      }}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? "bg-white" : "bg-white/30"
                      }`}
                      aria-label={`Gå til billede ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Swipe hint */}
              {images.length > 1 && (
                <p className="text-gray-500 text-center mt-2 text-xs md:hidden">
                  Swipe for at skifte billede
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ImageGallery;
